import React, { useState, useEffect, useRef } from 'react';
import Pagination from 'react-responsive-pagination';

import { RequestPage } from '../../api/request';
import { getParetos } from '../../api/requestSupervisor';
import { TopClientesXLS } from '../../views/XLS/TopClientesXLS';

import { formatNumber } from '../../utils/formats';

export const TopClientes = ({ topClientes = { data: [], last_page: 0, first_page_url: '' }, supervisor, loaded }) => {
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState(topClientes.data || []);

  // Paginación
  const [page, setPage] = useState('');
  const [totalPages, setTotalPages] = useState(topClientes.last_page || 0);
  const [urlPage, setUrlPage] = useState(topClientes.first_page_url || '');
  const [currentPage, setCurrentPage] = useState(topClientes.current_page || 0);

  // Mobile: control expand/collapse
  const [expanded, setExpanded] = useState({}); // map index -> bool

  const toggleExpanded = (idx) => {
    setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // ref al contenedor DOM donde renderiza TopClientesXLS -> para buscar su botón y hacer click
  const xlsContainerRef = useRef(null);

  const handlePageChange = async (pageNumber) => {
    setCurrentPage(pageNumber);
    if (!urlPage) return;
    if (supervisor) {
      const newUrl = urlPage.replace(/.$/, pageNumber + "&CodSupervisor=" + supervisor);
      await loadPage(newUrl);
    } else {
      const newUrl = urlPage.replace(/.$/, pageNumber);
      await loadPage(newUrl);
    }
  };

  const loadPage = async (pageData) => {
    try {
      setLoading(true);
      const clientesData = await RequestPage(pageData);
      setClientes(clientesData.data || []);
      setPage(clientesData.current_page);
      setCurrentPage(clientesData.current_page);
      setTotalPages(clientesData.last_page);
      setUrlPage(clientesData.first_page_url);
    } catch (err) {
      console.error('Error cargando página:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadParetosData = async (supervisorParam) => {
    try {
      setLoading(true);
      if (loaded === "todos") {
        if (topClientes?.data && topClientes.data.length > 0) {
          setClientes(topClientes.data);
          setTotalPages(topClientes.last_page || 0);
          setUrlPage(topClientes.first_page_url || '');
          return;
        }
      }

      let requestParetos;
      if (supervisorParam) {
        requestParetos = await getParetos(supervisorParam, null, null);
      } else {
        requestParetos = await getParetos(null, null, null);
      }

      if (requestParetos) {
        setClientes(requestParetos.data || []);
        setTotalPages(requestParetos.last_page || 0);
        setUrlPage(requestParetos.first_page_url || '');
        setCurrentPage(requestParetos.current_page || 1);
      }
    } catch (err) {
      console.error('Error cargando paretos:', err);
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    loadParetosData(supervisor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supervisor, loaded]);

  // ---------- Generar reporte (MOBILE) ----------
  const handleGenerateClick = () => {
    // buscamos dentro del contenedor DOM del componente TopClientesXLS
    const container = xlsContainerRef.current;
    if (!container) {
      console.warn('Contenedor XLS no montado aún.');
      alert('El control de exportación aún no está listo. Intenta de nuevo en unos segundos.');
      return;
    }

    // Prioridad: id conocido, clase conocida, data attribute, enlaces directos a xlsx/xls, o primer button encontrado.
    const selectors = [
      '#topclientes-xls-btn',
      '.topclientes-xls-btn',
      '[data-export="topclientes"]',
      'a[href$=".xlsx"]',
      'a[href$=".xls"]',
      'button[type="button"].download',
      'button'
    ];

    let found = null;
    for (const sel of selectors) {
      const el = container.querySelector(sel);
      if (el) { found = el; break; }
    }

    if (found) {
      // click programático para iniciar descarga
      try {
        found.click();
      } catch (err) {
        console.error('Error al intentar activar descarga desde móvil:', err);
        alert('No se pudo iniciar la descarga automáticamente. Por favor usa el control de exportación visible.');
      }
    } else {
      console.warn('No se encontró botón/anchor de descarga dentro de TopClientesXLS.');
      alert('No se encontró el control de descarga en esta vista. Verifica que TopClientesXLS renderice su botón.');
    }
  };

  return (
    <>
      {
        (loading) ?
          <div style={{ position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
            <div className="loader-book">
              <div>
                <ul>
                  <li>
                    <svg fill="currentColor" viewBox="0 0 90 120">
                      <path d="M90,0 L90,120 L11,120 C4.92486775,120 0,115.075132 0,109 L0,11 C0,4.92486775 4.92486775,0 11,0 L90,0 Z M71.5,81 L18.5,81 C17.1192881,81 16,82.1192881 16,83.5 C16,84.8254834 17.0315359,85.9100387 18.3356243,85.9946823 L18.5,86 L71.5,86 C72.8807119,86 74,84.8807119 74,83.5 C74,82.1745166 72.9684641,81.0899613 71.6643757,81.0053177 L71.5,81 Z M71.5,57 L18.5,57 C17.1192881,57 16,58.1192881 16,59.5 C16,60.8254834 17.0315359,61.9100387 18.3356243,61.9946823 L18.5,62 L71.5,62 C72.8807119,62 74,60.8807119 74,59.5 C74,58.1192881 72.8807119,57 71.5,57 Z M71.5,33 L18.5,33 C17.1192881,33 16,34.1192881 16,35.5 C16,36.8254834 17.0315359,37.9100387 18.3356243,37.9946823 L18.5,38 L71.5,38 C72.8807119,38 74,36.8807119 74,35.5 C74,34.1192881 72.8807119,33 71.5,33 Z"></path>
                    </svg>
                  </li>
                  <li>
                    <svg fill="currentColor" viewBox="0 0 90 120">
                      <path d="M90,0 L90,120 L11,120 C4.92486775,120 0,115.075132 0,109 L0,11 C0,4.92486775 4.92486775,0 11,0 L90,0 Z M71.5,81 L18.5,81 C17.1192881,81 16,82.1192881 16,83.5 C16,84.8254834 17.0315359,85.9100387 18.3356243,85.9946823 L18.5,86 L71.5,86 C72.8807119,86 74,84.8807119 74,83.5 C74,82.1745166 72.9684641,81.0899613 71.6643757,81.0053177 L71.5,81 Z M71.5,57 L18.5,57 C17.1192881,57 16,58.1192881 16,59.5 C16,60.8254834 17.0315359,61.9100387 18.3356243,61.9946823 L18.5,62 L71.5,62 C72.8807119,62 74,60.8807119 74,59.5 C74,58.1192881 72.8807119,57 71.5,57 Z M71.5,33 L18.5,33 C17.1192881,33 16,34.1192881 16,35.5 C16,36.8254834 17.0315359,37.9100387 18.3356243,37.9946823 L18.5,38 L71.5,38 C72.8807119,38 74,36.8807119 74,35.5 C74,34.1192881 72.8807119,33 71.5,33 Z"></path>
                    </svg>
                  </li>
                </ul>
              </div>
              <span>Cargando estadisticas...</span>
            </div>
          </div>
          :
          <>
            {/* Desktop XLS control (igual que antes) */}
            <div className="d-none d-md-block">
              <TopClientesXLS generate={loaded} />
            </div>

            <div className="row mt-3">
              {/* DESKTOP: tabla original */}
              <div className="col d-none d-md-block bg-white rounded drop-shadow-card">
                <h4 className='text-belmeny pt-2 ps-1'>Datos cargados: <span className="text-uppercase">{loaded}</span></h4>
                <div className="container-fluid mx-auto row bg-belmeny3 text-light mt-3 py-3 rounded-top">
                  <div className="col-2"><h5 className="text-uppercase">ZONA</h5></div>
                  <div className="col"><h5 className="text-uppercase text-center">CLIENTE</h5></div>
                  <div className="col-2"><h5 className="text-uppercase text-center">VENDEDOR</h5></div>
                  <div className="col-2"><h5 className="text-uppercase text-center">MÁXIMO FACTURADO</h5></div>
                  <div className="col-2"><h5 className="text-uppercase text-center">FACTURADO</h5></div>
                </div>

                {
                  (clientes && clientes.length > 0) ?
                    (clientes.map((cliente, idx) =>
                      <div className="row my-2 px-3 text-belmeny" key={cliente.CodigoCliente ?? idx}>
                        <div className="col-2"><h5>{cliente.Sector}</h5></div>
                        <div className="col"><h6 className='text-truncate' style={{ maxWidth: "450px" }}>{cliente.NombreCliente}</h6></div>
                        <div className="col-2 text-center"><h6>{cliente.NombreVendedor}</h6></div>
                        <div className="col-2 my-auto text-center">
                          <h6 className='text-success'>{formatNumber((cliente.Maximo_Facturado || 0).toFixed(2))} USD</h6>
                          <h6 className='text-muted fw-normal fst-italic text-center'>{cliente.Mes}</h6>
                        </div>
                        <div className="col-2 my-auto text-center">
                          <h6 className={cliente.Facturado < (cliente.Maximo_Facturado || 0) * 0.4 ? 'text-danger' : (cliente.Facturado < (cliente.Maximo_Facturado || 0) * 0.6 ? 'text-warning' : 'text-success')}>
                            {formatNumber((cliente.Facturado || 0).toFixed(2))} USD
                          </h6>
                          <h6 className='text-muted fw-normal fst-italic text-center'>Mes en curso</h6>
                        </div>
                      </div>
                    )) : <h4 className="text-center text-belmeny fst-italic mt-3 alert alert-info">No hay información de facturación de clientes por el momento...</h4>
                }

                <div className="division mt-4 mb-2"></div>
                <Pagination total={totalPages} current={currentPage} onPageChange={page => handlePageChange(page)} />
              </div>

              {/* MOBILE: vista apilada, compacta y expandible */}
              <div className="col d-block d-md-none">
                <style>{`
                  .topclientes-mobile .cliente-card { margin-bottom: 12px; }
                  .topclientes-mobile .cliente-header { font-weight: 700; }
                  .topclientes-mobile .cliente-sub { font-size: 0.85rem; color: #6c757d; }
                  .topclientes-mobile .expand-body { display: none; }
                  .topclientes-mobile .expand-body.show { display: block; margin-top: 10px; }
                  .topclientes-mobile .small-info { font-size: 0.85rem; color: #6c757d; }
                  .topclientes-mobile .xls-row { display:flex; justify-content:flex-end; gap:8px; margin-bottom:12px; }
                `}</style>

                <div className="m-auto bg-white p-3 rounded drop-shadow-card h-100 topclientes-mobile">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className='text-belmeny mb-0'>Datos: <span className="text-uppercase">{loaded}</span></h4>

                    {/* Botón visible en mobile para disparar la descarga */}
                    <div className="xls-row">
                      {/*<button className="btn btn-sm btn-primary" onClick={handleGenerateClick}>Generar Reporte (XLS)</button>*/}

                      {/* Renderizamos TopClientesXLS dentro de un contenedor con ref; esto deja el control visible (fallback) y nos permite buscar su botón */}
                      <div ref={xlsContainerRef} className="d-inline-block">
                        <TopClientesXLS generate={loaded} />
                      </div>
                    </div>
                  </div>

                  {clientes && clientes.length ? (
                    clientes.map((cliente, idx) => {
                      const isExpanded = !!expanded[idx];
                      return (
                        <div className="cliente-card" key={cliente.CodigoCliente ?? idx}>
                          <div
                            className="bg-light rounded p-2 d-flex justify-content-between align-items-center"
                            role="button"
                            onClick={() => toggleExpanded(idx)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleExpanded(idx); }}
                            tabIndex={0}
                          >
                            <div>
                              <div className="cliente-header">{cliente.NombreCliente}</div>
                              <div className="cliente-sub">{cliente.Sector} • {cliente.NombreVendedor}</div>
                            </div>
                            <div className="text-end">
                              <div className="small-info">{formatNumber((cliente.Maximo_Facturado || 0).toFixed(2))} USD</div>
                              <div className={cliente.Facturado < (cliente.Maximo_Facturado || 0) * 0.4 ? 'text-danger' : (cliente.Facturado < (cliente.Maximo_Facturado || 0) * 0.6 ? 'text-warning' : 'text-success')}>
                                {formatNumber((cliente.Facturado || 0).toFixed(2))} USD
                              </div>
                            </div>
                          </div>

                          <div className={`expand-body ${isExpanded ? 'show' : ''}`}>
                            <div className="p-2 bg-white rounded">
                              <div className="row">
                                <div className="col-6 small-info">Zona</div>
                                <div className="col-6">{cliente.Sector}</div>
                              </div>
                              <div className="row mt-1">
                                <div className="col-6 small-info">Vendedor</div>
                                <div className="col-6">{cliente.NombreVendedor}</div>
                              </div>
                              <div className="row mt-1">
                                <div className="col-6 small-info">Máximo facturado</div>
                                <div className="col-6 text-success">{formatNumber((cliente.Maximo_Facturado || 0).toFixed(2))} USD</div>
                              </div>
                              <div className="row mt-1">
                                <div className="col-6 small-info">Facturado</div>
                                <div className={`col-6 ${cliente.Facturado < (cliente.Maximo_Facturado || 0) * 0.4 ? 'text-danger' : (cliente.Facturado < (cliente.Maximo_Facturado || 0) * 0.6 ? 'text-warning' : 'text-success')}`}>
                                  {formatNumber((cliente.Facturado || 0).toFixed(2))} USD
                                </div>
                              </div>
                              <div className="row mt-1">
                                <div className="col-6 small-info">Mes</div>
                                <div className="col-6">{cliente.Mes}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-muted">No hay información de facturación de clientes por el momento...</div>
                  )}

                  <div className="division my-3" />
                  <Pagination total={totalPages} current={currentPage} onPageChange={page => handlePageChange(page)} />
                </div>
              </div>
            </div>
          </>
      }
    </>
  );
};
